<script setup lang="ts">
import { computed, ref } from "vue";
import { Plus } from "@lucide/vue";
import { ModelType } from "@/features/BuildingProject";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import SubCardHeading from "../SubCardHeading.vue";

const props = defineProps<{
  streamId: string;
  /** Every model name on the project, active or not, for the duplicate check. */
  existingModelNames: Array<string>;
}>();

const emit = defineEmits<{
  addNewModel: [model: ModelType];
}>();

const currentModelName = ref<string>("");

const trimmedName = computed(() => currentModelName.value.trim());
const isDuplicate = computed(() =>
  props.existingModelNames.some((existingName) => existingName.toLowerCase() === trimmedName.value.toLowerCase())
);
const errorMessage = computed(() => (isDuplicate.value ? `A model named "${trimmedName.value}" already exists.` : ""));
const canAdd = computed(() => trimmedName.value.length > 0 && !isDuplicate.value);

const handleAddCustomModel = () => {
  if (!canAdd.value) return;
  emit("addNewModel", {
    name: trimmedName.value,
    isActive: true,
    description: `Branch for stream - ${trimmedName.value} with id - ${props.streamId}`,
    streamId: props.streamId,
  });
  currentModelName.value = "";
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <SubCardHeading class="t-small-bold text-type-default">Create a model to publish into</SubCardHeading>
    <p class="t-caption text-type-muted max-w-prose">
      <template v-if="props.streamId">
        This names the model here; saving the project creates it on the stream. Geometry arrives when you publish to it from the
        Speckle connector.
      </template>
      <template v-else>
        This names the model here; it is written when you save the project. Connect a 3D model to publish geometry into it.
      </template>
      A model is usually one trade or discipline, for example Architecture, Structure or MEP. Names are saved in lower case, as
      Speckle stores them.
    </p>
    <div class="flex items-start gap-2">
      <div class="max-w-xs flex-1">
        <TextInputNew
          v-model="currentModelName"
          label="Model name"
          placeholder="e.g. Architecture"
          :error-msg="errorMessage"
          :remove-leading-whitespace="true"
          @keydown.enter.prevent="handleAddCustomModel" />
      </div>
      <button
        type="button"
        aria-label="Create model"
        class="focus-ring interactive-active duration-fast ease-standard rounded-8 t-small-bold text-type-default mt-6 flex h-9 shrink-0 items-center gap-1 bg-slate-200 px-4 whitespace-nowrap transition-colors hover:bg-slate-300"
        :class="{ 'interactive-disabled': !canAdd }"
        :disabled="!canAdd"
        @click="handleAddCustomModel">
        <Plus
          class="icon-sm"
          :stroke-width="1.5"
          aria-hidden="true" />
        <span>Create model</span>
      </button>
    </div>
  </div>
</template>
